// Course translations: text overlays per language, keyed by page, card and option keys.
// Mirrors Training::Translations in the API (same fields, same "missing" rule).

export const COURSE_LOCALES = ["nb", "en", "sv", "pl"];

const present = (value) => typeof value === "string" && value.trim() !== "";
const cardKeyOf = (card) => card.key;

/** Every translatable text in the draft, in reading order. */
export function translationFields({ guide, revision }) {
  const fields = [
    { id: "title", kind: "title", label: "trainingAdmin.translation.courseTitle", base: guide.title },
    { id: "description", kind: "description", label: "trainingAdmin.translation.courseDescription", base: guide.description, multiline: true },
  ];
  (revision.pages || []).forEach((page, pageIndex) => {
    fields.push({ id: `page:${page.key}`, kind: "page", pageKey: page.key, pageIndex, label: "trainingAdmin.translation.pageTitle", base: page.title });
    (page.cards || []).forEach((card) => {
      const cardKey = cardKeyOf(card);
      [
        ["title", "trainingAdmin.translation.cardTitle"],
        ["body", "trainingAdmin.translation.cardBody", { html: true }],
        ["explanation", "trainingAdmin.translation.explanation", { multiline: true }],
        ["image_alt", "trainingAdmin.translation.imageAlt"],
      ].forEach(([field, label, extra = {}]) =>
        fields.push({ id: `card:${cardKey}:${field}`, kind: "card", pageIndex, cardKey, field, label, ...extra, base: card[field] }),
      );
      (card.options || []).forEach((option, optionIndex) =>
        fields.push({ id: `option:${cardKey}:${option.id}`, kind: "option", pageIndex, cardKey, optionId: option.id, optionIndex, label: "trainingAdmin.translation.option", base: option.text }),
      );
    });
  });
  return fields.filter((field) => present(field.base));
}

export function readTranslation(overlay, field) {
  if (!overlay) return "";
  switch (field.kind) {
    case "title":
    case "description":
      return overlay[field.kind] || "";
    case "page":
      return overlay.pages?.[field.pageKey]?.title || "";
    case "card":
      return overlay.cards?.[field.cardKey]?.[field.field] || "";
    case "option":
      return overlay.cards?.[field.cardKey]?.options?.[field.optionId] || "";
    default:
      return "";
  }
}

export function writeTranslation(overlay = {}, field, value) {
  const next = { pages: {}, cards: {}, ...overlay };
  if (field.kind === "title" || field.kind === "description") return { ...next, [field.kind]: value };
  if (field.kind === "page") return { ...next, pages: { ...next.pages, [field.pageKey]: { title: value } } };
  const card = next.cards[field.cardKey] || {};
  if (field.kind === "card") return { ...next, cards: { ...next.cards, [field.cardKey]: { ...card, [field.field]: value } } };
  return { ...next, cards: { ...next.cards, [field.cardKey]: { ...card, options: { ...(card.options || {}), [field.optionId]: value } } } };
}

export const missingTranslations = (draft, overlay) =>
  translationFields(draft).filter((field) => !present(readTranslation(overlay, field)));

/** Fill only empty fields, so reviewed or hand-written text is never overwritten. */
export function mergeMissing(draft, overlay, suggestion) {
  return missingTranslations(draft, overlay).reduce((next, field) => {
    const value = readTranslation(suggestion, field);
    return present(value) ? writeTranslation(next, field, value) : next;
  }, overlay || {});
}

export const translatedLocales = (revision) =>
  COURSE_LOCALES.filter((code) => code !== (revision.content_locale || "nb") && revision.translations?.[code]);
